import { NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useConnectionStore } from '@/stores/connectionStore'
import { ThemeToggle } from '@/components/common/ThemeToggle'
import {
  ServerIcon,
  CodeBracketIcon,
  UsersIcon,
  UserGroupIcon,
  CogIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  ComputerDesktopIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline'
import { RiRobot3Line } from 'react-icons/ri'
import { clsx } from 'clsx'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  isMobile?: boolean
}

export function Sidebar({ collapsed, onToggle, isMobile = false }: SidebarProps) {
  const { user, logout } = useAuthStore()
  const { activeConnection } = useConnectionStore()
  const navigate = useNavigate()

  const handleNavClick = () => {
    // On mobile, auto-collapse sidebar after navigation for better space utilization
    if (isMobile && !collapsed) {
      // Small delay to allow navigation to complete
      setTimeout(() => { onToggle() }, 100)
    }
  }

  const menuItems = [
    {
      name: 'Connections',
      href: '/connections',
      icon: ServerIcon,
      description: 'Manage database connections',
    },
    {
      name: 'Explorer',
      href: activeConnection ? `/explorer/${activeConnection.id}` : '/connections',
      icon: CircleStackIcon,
      description: 'Browse database schema',
      disabled: !activeConnection,
    },
    {
      name: 'Query Editor',
      href: activeConnection ? `/query/${activeConnection.id}` : '/connections',
      icon: CodeBracketIcon,
      description: 'Execute SQL queries',
      disabled: !activeConnection,
    },
    {
      name: 'Users',
      href: '/users',
      icon: UsersIcon,
      description: 'User management',
      adminOnly: true,
    },
    {
      name: 'DB Users',
      href: activeConnection ? `/db-users/${activeConnection.id}` : '/connections',
      icon: UserGroupIcon,
      description: 'Database user management',
      disabled: !activeConnection,
    },
    {
      name: 'Sessions',
      href: activeConnection ? `/sessions/${activeConnection.id}` : '/connections',
      icon: ClockIcon,
      description: 'Active session management',
      disabled: !activeConnection,
    },
    {
      name: 'System',
      href: activeConnection ? `/system/${activeConnection.id}` : '/connections',
      icon: ComputerDesktopIcon,
      description: 'System information and variables',
      disabled: !activeConnection,
    },
    {
      name: 'Agents',
      href: '/agents',
      icon: RiRobot3Line,
      description: 'Manage database agents',
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: CogIcon,
      description: 'Application settings',
    },
  ]

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div
      className={clsx(
        'bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300',
        // Unified styling for both desktop and mobile - always show sidebar, just collapsed or expanded
        collapsed ? 'w-16' : 'w-64',
        // Mobile-specific positioning
        isMobile && 'fixed inset-y-0 left-0 z-50 md:relative'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        {!collapsed && (
          <div className="flex items-center space-x-2">
            <img 
              src="/images/mazpin/mazpin-logo.png" 
              alt="Mazpin Logo" 
              className="w-8 h-8"
            />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Mazpin</h1>
          </div>
        )}
        {collapsed && (
          <img 
            src="/images/mazpin/mazpin-logo.png" 
            alt="Mazpin Logo" 
            className="w-6 h-6"
          />
        )}
        <button type="button"
          onClick={onToggle}
          className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          {collapsed ? (
            <ChevronRightIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          ) : (
            <ChevronLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          )}
        </button>
      </div>

      {/* Active Connection */}
      {activeConnection && !collapsed && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Connected to</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {activeConnection.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {activeConnection.type} • {activeConnection.host}:{activeConnection.port}
            </p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className={clsx(
        "flex-1 overflow-y-auto py-4 space-y-1",
        collapsed ? "px-2" : "px-4"
      )}>
        {menuItems.map((item) => {
          if (item.adminOnly && user?.role !== 'admin') {
            return null
          }

          const isDisabled = item.disabled

          return (
            <NavLink
              key={item.name}
              to={item.href || '/'}
              className={({ isActive }) =>
                clsx(
                  'flex items-center px-3 rounded-md text-sm font-medium transition-colors',
                  collapsed ? 'py-2.5' : 'py-2',
                  isDisabled
                    ? 'text-gray-400 cursor-not-allowed'
                    : isActive
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
                  collapsed && 'justify-center'
                )
              }
              onClick={(e) => {
                if (isDisabled) {
                  e.preventDefault()
                } else {
                  handleNavClick()
                }
              }}
              title={collapsed ? item.name : ''}
            >
              <item.icon className={clsx('flex-shrink-0', collapsed ? 'w-5 h-5' : 'w-5 h-5 mr-3')} />
              {!collapsed && (
                <div>
                  <div>{item.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{item.description}</div>
                </div>
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        {/* Theme Toggle - Always visible */}
        <div className="flex justify-center mb-3">
          <ThemeToggle />
        </div>
        
        {!collapsed && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-primary-700">
                  {user?.authProvider && user.authProvider !== 'local'
                    ? ((user?.name && user.name.length > 0) ? user.name.charAt(0).toUpperCase() : 'U')
                    : ((user?.username && user.username.length > 0) ? user.username.charAt(0).toUpperCase() : 'U')}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.authProvider && user.authProvider !== 'local' 
                    ? (user?.name || 'User')
                    : (user?.username || 'User')}
                </p>
              </div>
            </div>
            <button type="button"
              onClick={handleLogout}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Logout
            </button>
          </div>
        )}
        {collapsed && (
          <button type="button"
            onClick={handleLogout}
            className="w-full flex justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Logout"
          >
            <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-medium text-primary-700">
                {user?.authProvider && user.authProvider !== 'local'
                  ? ((user?.name && user.name.length > 0) ? user.name.charAt(0).toUpperCase() : 'U')
                  : ((user?.username && user.username.length > 0) ? user.username.charAt(0).toUpperCase() : 'U')}
              </span>
            </div>
          </button>
        )}
      </div>
    </div>
  )
}
